import { useState } from 'react';
import { RiCloseFill, RiDeleteBin7Line } from 'react-icons/ri';
import * as Yup from 'yup';

import { showToast } from '../../../../components/CustomToast';
import { OutlinedInput } from '../../../../components/OutlinedInput';
import { OutlinedSelect } from '../../../../components/OutlinedSelect';
import { SolidButton } from '../../../../components/SolidButton';
import DropZone from '../../../../components/DropZone';
import { i18n } from '../../../../localization/i18n';
import api from '../../../../services/api';
import { typesErrors } from '../../../../utils/validateFormFields/yupErrors';
import validateFormFields from '../../../../utils/validateFormFields';
import {
  Container,
  Header,
  FormContainer,
  InputLine,
  InputContainer,
  Footer,
} from '../../../Admin/pages/Prices/components/CreatePrice/styles';
import { UploadFilesContainer, DropzoneContainer, File } from './styles';

interface ICreateProposalModalProps {
  onRequestClose: () => void;
}

interface IFormData {
  licensee_name: string;
  licensee_email: string;
  total_paid: number;
  fixcoins_to_add: number;
  payment_method: string;
}

const paymentMethodMocks = [
  { value: 'pix', optionText: 'PIX' },
  { value: 'boleto', optionText: 'Boleto' },
  { value: 'credit_card', optionText: 'Cartão de Crédito' },
  { value: 'not_applicable', optionText: 'Não se aplica' },
];

export function CreateBusinessProposalModal({
  onRequestClose,
}: ICreateProposalModalProps) {
  const [formData, setFormData] = useState<Partial<IFormData>>({});
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);

  const handleInputChange = (field: keyof IFormData, value: any) => {
    setFormData((prevState) => ({ ...prevState, [field]: value }));
  };

  async function handleSubmit() {
    setLoading(true);

    const bodyForValidation = {
      licensee_name: formData.licensee_name,
      licensee_email: formData.licensee_email,
      fixcoins_to_add: formData.fixcoins_to_add,
      documentFile: documentFile, // Valida o arquivo
    };
    const bodySchema = Yup.object().shape({
      licensee_name: Yup.string().required(typesErrors.required),
      licensee_email: Yup.string().email('Email inválido').required(typesErrors.required),
      fixcoins_to_add: Yup.number().typeError('Deve ser um número').required(typesErrors.required),
      documentFile: Yup.mixed().required('É obrigatório anexar o documento da proposta.'),
    });

    const validation = await validateFormFields(bodyForValidation, bodySchema, {
      licensee_name: "",
      licensee_email: "",
      fixcoins_to_add: "",
      documentFile: null,
    });

    if (validation.status === 400) {
      setErrors(validation.errors);
      showToast({ type: 'error', message: i18n.t('form_errors.alert') });
      setLoading(false);
      return;
    }
    setErrors({});

    const apiFormData = new FormData();
    apiFormData.append('licensee_name', formData.licensee_name || '');
    apiFormData.append('licensee_email', formData.licensee_email || '');
    if (formData.total_paid) apiFormData.append('total_paid', String(formData.total_paid));
    if (formData.fixcoins_to_add) apiFormData.append('fixcoins_to_add', String(formData.fixcoins_to_add));
    if (formData.payment_method) apiFormData.append('payment_method', formData.payment_method);
    if (documentFile) apiFormData.append('proposal_document', documentFile);

    try {
      await api.post('/business-proposal/invite', apiFormData);
      showToast({ type: 'success', message: 'Proposta criada com sucesso!' });
      onRequestClose();
    } catch (err: any) {
      console.error(err);
      const message = err?.response?.data?.message || 'Ocorreu um erro ao criar a proposta.';
      showToast({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container>
      <RiCloseFill size={24} color="var(--fixit)" onClick={onRequestClose} />
      <Header><span>Criar Nova Proposta Comercial</span></Header>
      <FormContainer>
        <InputLine>
          <InputContainer>
            <OutlinedInput
              label="Nome do Licenciado"
              inputName="licensee_name"
              value={formData.licensee_name ?? ''}
              handleChange={(val) => handleInputChange('licensee_name', val)}
              error={errors.licensee_name}
            />
          </InputContainer>
          <InputContainer>
            <OutlinedInput
              label="E-mail do Licenciado"
              inputName="licensee_email"
              type="email"
              value={formData.licensee_email ?? ''}
              handleChange={(val) => handleInputChange('licensee_email', val)}
              error={errors.licensee_email}
            />
          </InputContainer>
        </InputLine>
        <InputLine>
          <InputContainer>
            <OutlinedInput
              label="Valor Total Pago (Opcional)"
              inputName="total_paid"
              type="number"
              value={formData.total_paid ?? ''}
              handleChange={(val) => handleInputChange('total_paid', Number(val))}
              error={errors.total_paid}
            />
          </InputContainer>
          <InputContainer>
            <OutlinedInput
              label="FixCoins a Adicionar"
              inputName="fixcoins_to_add"
              type="number"
              value={formData.fixcoins_to_add ?? ''}
              handleChange={(val) =>
                handleInputChange('fixcoins_to_add', Number(val))
              }
              error={errors.fixcoins_to_add}
            />
          </InputContainer>
        </InputLine>
        <InputLine>
          <InputContainer>
            <OutlinedSelect
              label="Método de Pagamento (Opcional)"
              options={paymentMethodMocks}
              selectedOption={
                paymentMethodMocks.find(
                  (p) => p.value === formData.payment_method,
                )?.optionText || ''
              }
              handleSelect={(selected) =>
                handleInputChange('payment_method', selected.value)
              }
              error={errors.payment_method}
            />
          </InputContainer>
        </InputLine>

        <UploadFilesContainer>
          <label>Documento da Proposta (PDF)</label>
          <DropzoneContainer>
            <DropZone onUpload={(files) => setDocumentFile(files[0])} />
          </DropzoneContainer>

          {errors.documentFile && (
            <span style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
              {errors.documentFile}
            </span>
          )}

          {documentFile && (
            <File>
              <span>{documentFile.name}</span>
              <RiDeleteBin7Line
                onClick={() => setDocumentFile(null)}
                size={20}
                color="var(--fixit-danger)"
              />
            </File>
          )}
        </UploadFilesContainer>
      </FormContainer>
      <Footer>
        <SolidButton
          fullWidth
          text="Criar Proposta e Enviar Convite"
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        />
      </Footer>
    </Container>
  );
}
